import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { VideoIconButton } from './VideoIconButton';
import { formatTime } from '../utils/formatTime';

/**
 * @param {object} props
 * @param {boolean} props.canDisplayMuteBlock
 * @param {string} props.title
 * @param {boolean} props.isTouchSupported
 * @param {(muted: boolean) => void} props.clickUnmute
 * @param {(paused: boolean) => void} props.onPauseChange
 * @param {boolean} props.hasError
 * @param {{
 *   adPlaying: false | { duration: number };
 *   hasPlaybackStarted: boolean;
 *   time: number;
 *   duration: number;
 * } & Omit<import('@nbcnews/gamma').GammaProps, 'playlistItem' | 'jwPlayerLibraryUrl' | 'jwPlayerLibraryKey'>} props.videoState
 * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} props.handlePrevious
 * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} props.handleNext
 * @param {() => void} props.handleClose
 * @returns {JSX.Element}
 */
export function ControlOverlay({
  canDisplayMuteBlock,
  title,
  isTouchSupported,
  clickUnmute,
  onPauseChange,
  hasError,
  videoState,
  handlePrevious = () => {},
  handleNext = () => {},
  handleClose = () => {},
}) {
  const {
    isPaused, isMuted, time, duration, hasPlaybackStarted, adPlaying: adPlayingConfig,
  } = videoState;

  const adPlaying = Boolean(adPlayingConfig);

  const flooredPosition = Math.floor(time);
  const flooredDuration = Math.floor(duration);
  const flooredRemaining = flooredDuration - flooredPosition;
  const elapsedTimeLabel = formatTime(flooredPosition);
  const remainingTimeLabel = formatTime(flooredRemaining);

  const playbackNotStartedOrAdPlaying = !hasPlaybackStarted || adPlaying;
  const showPrevNextButtonGroup = (!playbackNotStartedOrAdPlaying || hasError) && !isTouchSupported;

  return (
    <div
      className={styles.controlContainer}
      role="region"
      aria-label="Video controls"
    >
      <div className={styles.controlSidebar}>
        <div className={styles.controlGroup}>
          <VideoIconButton
            type="close"
            ariaLabel="Close video"
            onClick={handleClose}
            className={styles.controlBtn}
          />
        </div>
        <div
          aria-hidden={playbackNotStartedOrAdPlaying && !hasError}
          className={classNames(styles.controlGroup, {
            [styles.hide]: !showPrevNextButtonGroup,
            [styles.visible]: showPrevNextButtonGroup,
          })}
        >
          <VideoIconButton
            type="previous"
            ariaLabel="Tap to go to previous video"
            onClick={(e) => {
              handlePrevious(e);
            }}
            className={styles.controlBtn}
          />
          <VideoIconButton
            type="next"
            ariaLabel="Tap to go to next video"
            onClick={(e) => {
              handleNext(e);
            }}
            className={styles.controlBtn}
          />
        </div>
        <div
          className={classNames(styles.controlGroup, {
            [styles.hide]: !hasPlaybackStarted,
            [styles.visible]: hasPlaybackStarted,
          })}
        >
          {isPaused ? (
            <VideoIconButton
              type="play"
              ariaLabel="Tap to play video"
              ariaHidden={!hasPlaybackStarted}
              onClick={() => {
                onPauseChange(!isPaused);
              }}
              className={styles.controlBtn}
            />
          ) : (
            <VideoIconButton
              type="pause"
              ariaLabel="Tap to pause video"
              ariaHidden={!hasPlaybackStarted}
              onClick={() => {
                onPauseChange(!isPaused);
              }}
              className={styles.controlBtn}
            />
          )}
          {canDisplayMuteBlock && (
            isMuted ? (
              <VideoIconButton
                type="muted"
                ariaLabel="Tap to unmute video"
                ariaHidden={adPlaying}
                onClick={() => {
                  clickUnmute(!isMuted);
                }}
                className={classNames(styles.controlBtn, {
                  [styles.hide]: adPlaying,
                  [styles.visible]: !adPlaying,
                })}
              />
            ) : (
              <VideoIconButton
                type="volume"
                ariaLabel="Tap to mute video"
                ariaHidden={adPlaying}
                onClick={() => {
                  clickUnmute(!isMuted);
                }}
                className={classNames(styles.controlBtn, {
                  [styles.hide]: adPlaying,
                  [styles.visible]: !adPlaying,
                })}
              />
            )
          )}
        </div>
      </div>
      <p className={styles.title}>{title}</p>
      <span className={styles.gradientOverlay} />
      <div className={classNames(styles.controlGroupTime, {
        [styles.hide]: playbackNotStartedOrAdPlaying,
        [styles.visible]: !playbackNotStartedOrAdPlaying,
      })}
      >
        <span aria-label={`Elapsed time ${elapsedTimeLabel}`} className={styles.elapsed}>{elapsedTimeLabel}</span>
        <span aria-label={`Remaining time ${remainingTimeLabel}`} className={styles.remaining}>{remainingTimeLabel}</span>
      </div>
    </div>
  );
}
